import * as THREE from 'three';
import smokeTrailVert from '../../glsl/smoke_trail_vert.glsl';
import smokeTrailFrag from '../../glsl/smoke_trail_frag.glsl';
import { createNoise4D } from 'simplex-noise';
import { SMOKE_TRAILS, RENDERING } from '../core/SimConfig.js';

// ── SmokeTrailSystem ────────────────────────────────────────────────────────
//
// Pool of camera-facing ribbon meshes. Each emitter owns a ring buffer of
// spine points (world position + age + per-point velocity for wind drift).
// Each spine point expands to two vertices in the shader (left/right of the
// trail) using a per-vertex tangent + side flag. The ribbon is rendered with
// the same hemisphere-tint lighting model as ParticleSystem.

const SCRATCH_TANGENT = new Float32Array(3);
const SCRATCH_DRIFT = new Float32Array(3);

export default class SmokeTrailSystem {
  /**
   * @param {{ scene: THREE.Scene, texture: THREE.Texture }} opts
   */
  constructor({ scene, texture }) {
    this.scene = scene;
    this._texture = texture;

    const N = SMOKE_TRAILS.poolSize;
    const M = SMOKE_TRAILS.maxPoints;
    this._maxPoints = M;
    this._poolSize = N;

    // Shared sky-tint uniforms — propagated to every emitter material in
    // setLighting(). Stored here so newly spawned emitters inherit current.
    this._skyTop = new THREE.Vector3(1, 1, 1);
    this._skyBottom = new THREE.Vector3(0.3, 0.3, 0.3);
    // Per-scenario dim for the env-lit term only (emissive trails unaffected).
    this._effectBrightness = 1;

    // Free-list (stack) for O(1) alloc
    this._freeList = new Uint16Array(N);
    this._freeCount = N;
    for (let i = 0; i < N; i++) this._freeList[i] = N - 1 - i;

    // Fractal drift field — three independent 4D simplex noise functions
    // (x/y/z velocity components; the 4th axis is time). Sampled at each
    // anchor's world position every frame and summed over several octaves to
    // perturb the smoke with smooth, spatially-coherent swirls instead of
    // per-anchor white noise. See _driftVelocity().
    this._driftNoiseX = createNoise4D();
    this._driftNoiseY = createNoise4D();
    this._driftNoiseZ = createNoise4D();
    this._t = 0;

    // Per-emitter slots — each slot owns its own ring buffer + Mesh.
    this._slots = new Array(N);
    for (let i = 0; i < N; i++) {
      this._slots[i] = this._createSlot(M);
    }

    // Every slot's ribbon mesh, for App to move on/off the FX overlay layer (see
    // App._syncFXOverlay) — held as one array so that runs allocation-free. The
    // whole pool is tagged, live or not; an idle slot is `visible = false` and
    // costs nothing wherever it's parked.
    this.ribbonMeshes = this._slots.map((s) => s.mesh);
  }

  // ── Slot factory ──────────────────────────────────────────────────────────
  //
  // A slot is a fully wired ribbon: typed-array ring buffer (CPU side) +
  // BufferGeometry (GPU side) + Mesh. Pre-allocated once; reused on respawn.

  _createSlot(M) {
    // Trail state:
    //   - "head" is a live point that tracks the emitter every advance() call.
    //     It is rendered as the leading vertex but is NOT stored in the anchor
    //     ring. This means small frame-to-frame motion (dist < pointSpacing)
    //     still produces a renderable [head, newest_anchor] segment instead of
    //     getting absorbed into a single coincident point.
    //   - "anchors" are immutable once written. A new anchor is appended at
    //     the head's position when the head moves ≥ pointSpacing from the
    //     most recent anchor.
    const cpu = {
      // Anchor ring (each entry is a fixed historical point on the trail).
      // Drift turbulence is no longer stored per-anchor; it's sampled from a
      // fractal noise field at the anchor's live position each frame (see
      // _driftVelocity), so adjacent puffs swirl together coherently.
      // du is the texture-v coordinate (in meters of arc length from spawn)
      // captured when the anchor was promoted. Stored once and never updated,
      // so the texture stays locked to the smoke instead of sliding along the
      // ribbon as the head moves.
      px: new Float32Array(M),
      py: new Float32Array(M),
      pz: new Float32Array(M),
      age: new Float32Array(M),
      du: new Float32Array(M),
      anchorHead: 0,                    // index of newest anchor in the ring
      anchorCount: 0,                   // number of live anchors

      // Live head (tracks the emitter; not in the ring)
      headX: 0, headY: 0, headZ: 0,
      headInit: false,                  // true once advance() has seen at least one call
    };

    // GPU buffers — 2 vertices per spine point (left/right), indexed strip.
    const V = M * 2;
    const positions = new Float32Array(V * 3);
    const tangents  = new Float32Array(V * 3);
    const sides     = new Float32Array(V);
    const ages      = new Float32Array(V);
    const distUs    = new Float32Array(V);
    const alives    = new Float32Array(V);
    const leadFades = new Float32Array(V);

    // Pre-bake side attribute (-1, +1 per pair) and triangle index buffer.
    for (let i = 0; i < M; i++) {
      sides[i * 2]     = -1;
      sides[i * 2 + 1] = +1;
    }
    // Two triangles per quad between consecutive spine points: M-1 quads max.
    const indices = new Uint16Array((M - 1) * 6);
    for (let i = 0; i < M - 1; i++) {
      const a = i * 2;
      const b = a + 1;
      const c = a + 2;
      const d = a + 3;
      const o = i * 6;
      indices[o]     = a;
      indices[o + 1] = b;
      indices[o + 2] = c;
      indices[o + 3] = b;
      indices[o + 4] = d;
      indices[o + 5] = c;
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('aTangent', new THREE.BufferAttribute(tangents, 3));
    geom.setAttribute('aSide',    new THREE.BufferAttribute(sides, 1));
    geom.setAttribute('aAge01',   new THREE.BufferAttribute(ages, 1));
    geom.setAttribute('aDistU',   new THREE.BufferAttribute(distUs, 1));
    geom.setAttribute('aAlive',   new THREE.BufferAttribute(alives, 1));
    geom.setAttribute('aLeadFade', new THREE.BufferAttribute(leadFades, 1));
    geom.setIndex(new THREE.BufferAttribute(indices, 1));
    geom.getAttribute('position').setUsage(THREE.DynamicDrawUsage);
    geom.getAttribute('aTangent').setUsage(THREE.DynamicDrawUsage);
    geom.getAttribute('aAge01').setUsage(THREE.DynamicDrawUsage);
    geom.getAttribute('aDistU').setUsage(THREE.DynamicDrawUsage);
    geom.getAttribute('aAlive').setUsage(THREE.DynamicDrawUsage);
    geom.getAttribute('aLeadFade').setUsage(THREE.DynamicDrawUsage);
    geom.setDrawRange(0, 0);

    const mat = new THREE.RawShaderMaterial({
      uniforms: {
        uTex:           { value: this._texture },
        uColor:         { value: new THREE.Vector3(1, 1, 1) },
        uStartWidth:    { value: 1 },
        uEndWidth:      { value: 1 },
        uStartOpacity:  { value: 1 },
        uEndOpacity:    { value: 0 },
        uEmissive:      { value: 0 },
        uLogDepthBufFC: { value: 2.0 / (Math.log(RENDERING.farPlane + 1.0) / Math.LN2) },
        uNearFadeStart: { value: SMOKE_TRAILS.nearFadeStart },
        uNearFadeEnd:   { value: SMOKE_TRAILS.nearFadeEnd },
        uSkyTop:        { value: this._skyTop.clone() },
        uSkyBottom:     { value: this._skyBottom.clone() },
        uEffectBrightness: { value: this._effectBrightness },
      },
      glslVersion: THREE.GLSL3,
      vertexShader: `
        precision highp float;
        ${smokeTrailVert}
      `,
      fragmentShader: `
        precision highp float;
        ${smokeTrailFrag}
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geom, mat);
    mesh.frustumCulled = false;
    mesh.visible = false;
    this.scene.add(mesh);

    return {
      cpu,
      mesh,
      geom,
      mat,
      // Live config (set on spawn)
      active: false,       // true while a handle is held
      releasing: false,    // true after release() — stops appending, ages out
      length: 1,           // seconds — points older than this are dropped
      pointSpacing: 1,     // meters between spine points
      textureLength: 32,   // meters per full v-tile
      drift: true,
      driftJitter: 0,      // m/s — amplitude of the fractal-noise turbulence added to wind
      generation: 0,       // bumped on each release so stale handles don't act
    };
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Spawn a new emitter. Returns a handle (packed slot+generation) usable
   * with advance/release, or -1 if the pool is exhausted.
   *
   * @param {{
   *   color?: [number,number,number],
   *   startOpacity?: number,
   *   endOpacity?: number,
   *   startWidth?: number,
   *   endWidth?: number,
   *   length?: number,           // seconds
   *   pointSpacing?: number,     // meters between spine points
   *   textureLength?: number,    // meters per full vertical tile of the texture
   *   emissive?: number,
   *   drift?: boolean,
   * }} cfg
   */
  spawn(cfg) {
    if (this._freeCount === 0) return -1;
    const slotIdx = this._freeList[--this._freeCount];
    const s = this._slots[slotIdx];

    s.active = true;
    s.releasing = false;
    s.length = cfg.length ?? 2.0;
    s.pointSpacing = cfg.pointSpacing ?? 1.0;
    s.textureLength = cfg.textureLength ?? 32.0;
    s.drift = cfg.drift ?? true;
    s.driftJitter = cfg.driftJitter ?? 0;

    s.cpu.anchorHead = 0;
    s.cpu.anchorCount = 0;
    s.cpu.headInit = false;

    const u = s.mat.uniforms;
    const c = cfg.color ?? [1, 1, 1];
    u.uColor.value.set(c[0], c[1], c[2]);
    u.uStartOpacity.value = cfg.startOpacity ?? 1.0;
    u.uEndOpacity.value   = cfg.endOpacity   ?? 0.0;
    u.uStartWidth.value   = cfg.startWidth   ?? 1.0;
    u.uEndWidth.value     = cfg.endWidth     ?? 1.0;
    u.uEmissive.value     = cfg.emissive     ?? 0.0;
    u.uSkyTop.value.copy(this._skyTop);
    u.uSkyBottom.value.copy(this._skyBottom);

    s.mesh.visible = true;
    s.geom.setDrawRange(0, 0);

    return (s.generation << 16) | slotIdx;
  }

  /**
   * Append the current emitter head position. Call once per frame while the
   * emitter is alive. New spine points are added when the head has moved at
   * least `pointSpacing` meters from the last point.
   *
   * @param {number} handle — value returned from spawn()
   * @param {number} x, y, z — current head world position
   * @param {number} vx, vy, vz — current head world velocity (for new point bookkeeping)
   */
  advance(handle, x, y, z, vx, vy, vz) {
    if (handle < 0) return;
    const slotIdx = handle & 0xffff;
    const gen = handle >>> 16;
    const s = this._slots[slotIdx];
    if (!s.active || s.releasing || s.generation !== gen) return;

    const cpu = s.cpu;
    const M = this._maxPoints;

    // First call: seed the first anchor at the emitter pos and start the head.
    if (!cpu.headInit) {
      cpu.headX = x; cpu.headY = y; cpu.headZ = z;
      cpu.headInit = true;
      cpu.anchorHead = 0;
      cpu.anchorCount = 1;
      cpu.px[0] = x; cpu.py[0] = y; cpu.pz[0] = z;
      cpu.age[0] = 0;
      cpu.du[0] = 0;
      return;
    }

    // The head always tracks the emitter (no distance threshold for this).
    cpu.headX = x; cpu.headY = y; cpu.headZ = z;

    // Promote: when the head has moved >= pointSpacing from the most recent
    // anchor, freeze a new anchor at the current emitter position.
    const aIdx = cpu.anchorHead;
    const dx = x - cpu.px[aIdx];
    const dy = y - cpu.py[aIdx];
    const dz = z - cpu.pz[aIdx];
    const dist2 = dx * dx + dy * dy + dz * dz;
    const sp = s.pointSpacing;
    if (dist2 < sp * sp) return;

    // Advance one anchor per spacing-unit travelled — keeps anchor density
    // uniform even on a frame that covers many spacings.
    const dist = Math.sqrt(dist2);
    const steps = Math.min(M, Math.max(1, Math.floor(dist / sp)));
    const sx = cpu.px[aIdx], sy = cpu.py[aIdx], sz = cpu.pz[aIdx];
    const baseDu = cpu.du[aIdx];
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const ni = (cpu.anchorHead + 1) % M;
      cpu.anchorHead = ni;
      cpu.px[ni] = sx + dx * t;
      cpu.py[ni] = sy + dy * t;
      cpu.pz[ni] = sz + dz * t;
      cpu.age[ni] = 0;
      cpu.du[ni] = baseDu + dist * t;
      if (cpu.anchorCount < M) cpu.anchorCount++;
    }
  }

  /**
   * Fractal (fBm) drift velocity at a world position, in m/s. Sums several
   * octaves of 4D simplex noise (lacunarity 2, gain 0.5) per axis — the higher
   * octaves add fine detail while the base octave gives the broad swirl. The
   * 4th noise axis is scaled time so the field slowly evolves. Result per axis
   * is normalized to ~[-1, 1] then scaled by `amp` (the preset's driftJitter).
   */
  _driftVelocity(x, y, z, amp, out) {
    const cfg = SMOKE_TRAILS.drift;
    const sf = cfg.spatialFrequency;
    const octaves = cfg.octaves;
    const w = this._t * cfg.timeFrequency;
    const nx = x * sf, ny = y * sf, nz = z * sf;

    let sx = 0, sy = 0, sz = 0;
    let ampO = 1, freqO = 1, norm = 0;
    for (let o = 0; o < octaves; o++) {
      sx += ampO * this._driftNoiseX(nx * freqO, ny * freqO, nz * freqO, w * freqO);
      sy += ampO * this._driftNoiseY(nx * freqO, ny * freqO, nz * freqO, w * freqO);
      sz += ampO * this._driftNoiseZ(nx * freqO, ny * freqO, nz * freqO, w * freqO);
      norm += ampO;
      ampO *= 0.5;
      freqO *= 2;
    }
    const inv = amp / norm;
    out[0] = sx * inv;
    out[1] = sy * inv;
    out[2] = sz * inv;
  }

  /**
   * Release the emitter. Existing anchors keep aging out; the slot frees once
   * empty. Subsequent advance() calls with this handle become no-ops.
   *
   * Drops a final anchor at the current head position so the trail's leading
   * tip captures the missile's actual death location, not just the last
   * promoted anchor.
   */
  release(handle) {
    if (handle < 0) return;
    const slotIdx = handle & 0xffff;
    const gen = handle >>> 16;
    const s = this._slots[slotIdx];
    if (!s.active || s.generation !== gen) return;

    const cpu = s.cpu;
    const M = this._maxPoints;
    if (cpu.headInit && cpu.anchorCount > 0) {
      const aIdx = cpu.anchorHead;
      const dx = cpu.headX - cpu.px[aIdx];
      const dy = cpu.headY - cpu.py[aIdx];
      const dz = cpu.headZ - cpu.pz[aIdx];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > 0.01) {
        const ni = (cpu.anchorHead + 1) % M;
        const baseDu = cpu.du[aIdx];
        cpu.anchorHead = ni;
        cpu.px[ni] = cpu.headX; cpu.py[ni] = cpu.headY; cpu.pz[ni] = cpu.headZ;
        cpu.age[ni] = 0;
        cpu.du[ni] = baseDu + Math.sqrt(d2);
        if (cpu.anchorCount < M) cpu.anchorCount++;
      }
    }
    cpu.headInit = false;
    s.releasing = true;
  }

  // ── Per-frame update ──────────────────────────────────────────────────────

  /**
   * Age all live points, advect by wind, expire tail, and rewrite GPU buffers.
   * @param {number} dt
   * @param {THREE.Camera} _camera — accepted for symmetry with ParticleSystem
   * @param {THREE.Vector3} [wind]
   */
  update(dt, _camera, wind) {
    this._t += dt;

    const windX = wind ? wind.x : 0;
    const windY = wind ? wind.y : 0;
    const windZ = wind ? wind.z : 0;

    const M = this._maxPoints;
    const slots = this._slots;
    const drift = SCRATCH_DRIFT;

    for (let si = 0; si < slots.length; si++) {
      const s = slots[si];
      if (!s.active) continue;

      const cpu = s.cpu;
      const lifetime = s.length;

      // Age + advect every anchor. The head tracks the live emitter via
      // advance(), so it has no age and isn't aged here. Drift adds the
      // global wind plus a fractal-noise turbulence velocity sampled at each
      // anchor's live position, so neighbouring puffs swirl together instead
      // of jittering independently.
      const jitter = s.drift ? s.driftJitter : 0;
      for (let i = 0; i < cpu.anchorCount; i++) {
        const idx = (cpu.anchorHead - i + M) % M;
        cpu.age[idx] += dt;
        if (s.drift) {
          let dvx = 0, dvy = 0, dvz = 0;
          if (jitter > 0) {
            this._driftVelocity(cpu.px[idx], cpu.py[idx], cpu.pz[idx], jitter, drift);
            dvx = drift[0]; dvy = drift[1]; dvz = drift[2];
          }
          cpu.px[idx] += (windX + dvx) * dt;
          cpu.py[idx] += (windY + dvy) * dt;
          cpu.pz[idx] += (windZ + dvz) * dt;
        }
      }

      // Expire oldest anchors (tail of the ring)
      while (cpu.anchorCount > 0) {
        const tailIdx = (cpu.anchorHead - (cpu.anchorCount - 1) + M) % M;
        if (cpu.age[tailIdx] >= lifetime) {
          cpu.anchorCount--;
        } else {
          break;
        }
      }

      // Slot is empty when the emitter is released and no anchors remain
      const renderable = cpu.anchorCount + (cpu.headInit ? 1 : 0);
      if (renderable === 0) {
        if (s.releasing) {
          s.active = false;
          s.releasing = false;
          s.generation = (s.generation + 1) & 0x7fff;
          s.mesh.visible = false;
          s.geom.setDrawRange(0, 0);
          this._freeList[this._freeCount++] = si;
        }
        continue;
      }

      this._writeGpuBuffers(s);
    }
  }

  // ── Per-slot GPU buffer writer ────────────────────────────────────────────

  _writeGpuBuffers(s) {
    const cpu = s.cpu;
    const M = this._maxPoints;
    const lifetime = s.length;
    const invLife = 1 / Math.max(lifetime, 1e-3);
    const invTexLen = 1 / Math.max(s.textureLength, 1e-3);

    // Render order, newest → oldest:
    //   index 0       = head (live emitter pos), if headInit
    //   index 1..A    = anchors[anchorHead .. anchorHead - (A-1)]  (ring-walked)
    // Total = A + (headInit ? 1 : 0). Single helper to read a logical position.
    const A = cpu.anchorCount;
    const hasHead = cpu.headInit;
    const N = A + (hasHead ? 1 : 0);

    // Head's u coordinate: anchorHead's stored du plus current head offset.
    // Anchors keep the du they were born with, so their texture v never moves
    // along the ribbon — only the leading head segment can stretch a little.
    let headDu = 0;
    if (hasHead && A > 0) {
      const ah = cpu.anchorHead;
      const dx = cpu.headX - cpu.px[ah];
      const dy = cpu.headY - cpu.py[ah];
      const dz = cpu.headZ - cpu.pz[ah];
      headDu = cpu.du[ah] + Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    const getX = (i) => (hasHead && i === 0) ? cpu.headX : cpu.px[(cpu.anchorHead - (i - (hasHead ? 1 : 0)) + M) % M];
    const getY = (i) => (hasHead && i === 0) ? cpu.headY : cpu.py[(cpu.anchorHead - (i - (hasHead ? 1 : 0)) + M) % M];
    const getZ = (i) => (hasHead && i === 0) ? cpu.headZ : cpu.pz[(cpu.anchorHead - (i - (hasHead ? 1 : 0)) + M) % M];
    // Head age = 0 (always fresh); anchor ages live in cpu.age.
    const getAge = (i) => (hasHead && i === 0) ? 0 : cpu.age[(cpu.anchorHead - (i - (hasHead ? 1 : 0)) + M) % M];
    const getDu  = (i) => (hasHead && i === 0) ? headDu : cpu.du[(cpu.anchorHead - (i - (hasHead ? 1 : 0)) + M) % M];

    const positions  = s.geom.getAttribute('position').array;
    const tangents   = s.geom.getAttribute('aTangent').array;
    const ages       = s.geom.getAttribute('aAge01').array;
    const distUs     = s.geom.getAttribute('aDistU').array;
    const alives     = s.geom.getAttribute('aAlive').array;
    const leadFades  = s.geom.getAttribute('aLeadFade').array;

    // Walk newest (i = 0) to oldest. Texture v comes from each anchor's stored
    // du so the texture stays nailed to the smoke; the head fades in via
    // aLeadFade so the ribbon's leading edge isn't a hard line.
    for (let i = 0; i < N; i++) {
      const x = getX(i), y = getY(i), z = getZ(i);

      // Tangent: central difference where possible, forward/backward at ends
      let tx, ty, tz;
      if (N === 1) {
        tx = 1; ty = 0; tz = 0;
      } else if (i === 0) {
        tx = x - getX(1);
        ty = y - getY(1);
        tz = z - getZ(1);
      } else if (i === N - 1) {
        tx = getX(i - 1) - x;
        ty = getY(i - 1) - y;
        tz = getZ(i - 1) - z;
      } else {
        tx = getX(i - 1) - getX(i + 1);
        ty = getY(i - 1) - getY(i + 1);
        tz = getZ(i - 1) - getZ(i + 1);
      }
      const tLen = Math.sqrt(tx * tx + ty * ty + tz * tz);
      if (tLen > 1e-6) { tx /= tLen; ty /= tLen; tz /= tLen; }
      else { tx = 1; ty = 0; tz = 0; }

      const age01 = Math.min(1, getAge(i) * invLife);
      const distU = getDu(i) * invTexLen;
      // Fade both ends of the ribbon: the head (i=0, when present) and the
      // oldest tail anchor (i = N-1) so neither end shows a hard line.
      const isHead = hasHead && i === 0;
      const isTail = i === N - 1 && N > 1;
      const leadFade = (isHead || isTail) ? 0 : 1;

      // Two vertices per spine point — in spine order (i = 0 is the newest)
      const vBase = i * 2;
      const p0 = vBase * 3;
      const p1 = (vBase + 1) * 3;

      positions[p0]      = x; positions[p0 + 1] = y; positions[p0 + 2] = z;
      positions[p1]      = x; positions[p1 + 1] = y; positions[p1 + 2] = z;
      tangents[p0]       = tx; tangents[p0 + 1] = ty; tangents[p0 + 2] = tz;
      tangents[p1]       = tx; tangents[p1 + 1] = ty; tangents[p1 + 2] = tz;
      ages[vBase]        = age01;
      ages[vBase + 1]    = age01;
      distUs[vBase]      = distU;
      distUs[vBase + 1]  = distU;
      alives[vBase]      = 1;
      alives[vBase + 1]  = 1;
      leadFades[vBase]     = leadFade;
      leadFades[vBase + 1] = leadFade;
    }

    // Mark trailing (unused) vertices as dead so any leftover triangles collapse
    if (N < this._maxPoints) {
      const startV = N * 2;
      const endV = this._maxPoints * 2;
      for (let v = startV; v < endV; v++) alives[v] = 0;
    }

    s.geom.getAttribute('position').needsUpdate = true;
    s.geom.getAttribute('aTangent').needsUpdate = true;
    s.geom.getAttribute('aAge01').needsUpdate = true;
    s.geom.getAttribute('aDistU').needsUpdate = true;
    s.geom.getAttribute('aAlive').needsUpdate = true;
    s.geom.getAttribute('aLeadFade').needsUpdate = true;

    // Triangle count: 2 per quad between consecutive points (N-1 quads).
    const tris = Math.max(0, N - 1) * 6;
    s.geom.setDrawRange(0, tris);
  }

  // ── Lighting ──────────────────────────────────────────────────────────────

  /**
   * Update environment lighting on all emitter materials. Mirrors
   * ParticleSystem.setLighting.
   * @param {{ top: number[], bottom: number[] }} skyTint — linear RGB
   * @param {number} [effectBrightness=1] — env-lit dim multiplier (emissive untouched)
   */
  setLighting(skyTint, effectBrightness = 1) {
    const t = skyTint?.top ?? [1, 1, 1];
    const b = skyTint?.bottom ?? [0.3, 0.3, 0.3];
    this._skyTop.set(t[0], t[1], t[2]);
    this._skyBottom.set(b[0], b[1], b[2]);
    this._effectBrightness = effectBrightness;
    for (let i = 0; i < this._slots.length; i++) {
      const u = this._slots[i].mat.uniforms;
      u.uSkyTop.value.set(t[0], t[1], t[2]);
      u.uSkyBottom.value.set(b[0], b[1], b[2]);
      u.uEffectBrightness.value = effectBrightness;
    }
  }

  // ── Stats ─────────────────────────────────────────────────────────────────

  get aliveCount() {
    let n = 0;
    for (let i = 0; i < this._slots.length; i++) if (this._slots[i].active) n++;
    return n;
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────

  dispose() {
    for (let i = 0; i < this._slots.length; i++) {
      const s = this._slots[i];
      this.scene.remove(s.mesh);
      s.geom.dispose();
      s.mat.dispose();
    }
    this._slots.length = 0;
  }
}
